import { AutoMarkType } from "services/shortAnswerService/question-types/common";
import { AnyQuestionI } from "services/shortAnswerService/question-types/Question";
import {
  isMultiModelQuestion,
  isMultiReasonQuestion,
  isSimpleQuestion,
  isTextQuestion
} from "services/shortAnswerService/question-types/typeGuards";

export type SectionWithSubSections = {
  sectionId: string;
  leafSubSectionIds: string[];
};

export type SectionWithQuestions = {
  sectionId: string;
  questions: AnyQuestionI[];
};

export type FlatSectionWithQuestion = {
  question: AnyQuestionI;
  sectionId: string;
  selected?: boolean;
};

export function removeDuplicateQuestions(questions: FlatSectionWithQuestion[]) {
  return questions.reduce((acc: FlatSectionWithQuestion[], question) => {
    if (acc.find(q => q.question.questionId === question.question.questionId))
      return acc;
    return [...acc, question];
  }, []);
}

export function groupQuestionBySection(
  questionsToGroup: FlatSectionWithQuestion[]
) {
  return questionsToGroup.reduce((acc: SectionWithQuestions[], question) => {
    if (question) {
      const existingQuestionSection = acc.find(
        existingQuestion => existingQuestion.sectionId === question.sectionId
      );
      if (existingQuestionSection) {
        existingQuestionSection.questions.push(question.question);
      } else {
        acc.push({
          sectionId: question.sectionId,
          questions: [question.question]
        });
      }
    }
    return acc;
  }, []);
}

export function getOneQuestionFromEachSectionOnCondition(
  questions: FlatSectionWithQuestion[],
  condition: (questions: FlatSectionWithQuestion) => boolean
) {
  const uniqueSectionIds = [...new Set(questions.map(q => q.sectionId))];

  return uniqueSectionIds.reduce(
    (acc: FlatSectionWithQuestion[], sectionId) => {
      const candidate = questions.find(
        question =>
          question.sectionId === sectionId &&
          !acc.find(
            q => q.question.questionId === question.question.questionId
          ) &&
          condition(question)
      );

      if (candidate) return [...acc, candidate];
      return acc;
    },
    []
  );
}

export function getTagTone(mark: number, marksTotal: number) {
  if (marksTotal === 0) {
    return "error";
  }
  const percent = mark / marksTotal;
  if (percent === 0) return "error";
  else if (percent > 0 && percent < 1) return "warning";
  else return "success";
}

export function getIsAutoMarkingAvailable({
  autoMarkingEnabled,
  question
}: {
  autoMarkingEnabled: boolean;
  question?: AnyQuestionI;
}) {
  return !!question && autoMarkingEnabled && questionHasAutoMarking(question);
}

export function questionHasAutoMarking(question: AnyQuestionI) {
  return (
    isTextQuestion(question) && question.autoMarkType !== AutoMarkType.NONE
  );
}

export function getActualMark(questionStats?: {
  teacherMark?: number;
  selfMark?: number;
  mark?: number;
}) {
  return (
    questionStats?.teacherMark ?? questionStats?.selfMark ?? questionStats?.mark
  );
}

export function getInputCount(question: AnyQuestionI) {
  if (isSimpleQuestion(question)) {
    return 1;
  }

  if (isMultiModelQuestion(question)) {
    return question.embeddedQuestions.length;
  }

  if (isMultiReasonQuestion(question)) {
    return question.inputMetas.length;
  }
}
